import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import { useQuiz } from './hooks';
import { ScreenTypes } from "../../store/quiz/quizSlice";
import QuizDetailsScreen from './Detail';
import QuizDiagnosticDetail from './DetailDiagnostic';
import QuestionScreen from './Questions';
import Results from './Results';
import { assessments, getById } from '../../assets/data/assessments';
import QuizComplete from './Complete';
import Evalating from './Evaluating';
import { logoutUser } from '../../utils';
import Loader from '../../components/Loader';
import { getAssessmentAnswersActivity } from '../../store/quiz/quizBaseApi';
import { getAssessmentAnswers, saveAssessmentAnswers, clearAssessmentAnswers, getAssessmentResult } from '../../utils/storageHelper';

function Quiz({ type, data, title, onComplete, mandatePassing, onFail, style, mandateSpeechEvaluation, setAssessmentBegin, setAssessmentEnded, onConfettiClose, setAssessmentScreen }) {
    const { id } = useParams();
    const params = useSearchParams();
    const { current, setScreen, setQuestions, setQuizTopic, setQuizDetails, setType, setResult, questions, setGradingResult, setSpeechEvaluationScore, setEvaluationResult } = useQuiz();
    const ref = useRef(null);
    const [loading, setLoading] = useState(true);

    const quizType = params[0].get('type') || type;
    useEffect(() => {
        if (data.preview) {
            setScreen(ScreenTypes.QuestionScreen)
        } else {
            // setTimeout(() => {
            setScreen(ScreenTypes.QuizDetail)
            // }, 1000)
        }
    }, [setScreen]);

    useEffect(() => {
        if (!id && !data && Object.keys(data).length) return;

        if (questions.length) {
            setQuestions([]);
            setQuizDetails({});
            setResult([]);
        }

        const assessment = data || getById(id);

        let { topic, timeAllowed, totalMarks, noOfQuestions } = assessment;
        let _questions = assessment.questions;
        let selectedQuizTopic = topic;
        let questionGroups = new Map();

        _questions?.forEach(e => {
            if (e.groupDetails && !questionGroups.has(e.groupDetails.id)) {
                questionGroups.set(e.groupDetails.id, e.groupDetails);
            }
        })

        setQuestions(_.shuffle(_questions));
        setQuizTopic(title || topic);

        setQuizDetails({
            ...assessment,
            totalMarks: Number(totalMarks),
            noOfQuestions: noOfQuestions,
            selectedQuizTopic,
            totalTime: Number(timeAllowed) * 60,
        });

        setType(quizType);
        setGradingResult({ grade: null, passed: null, timeTaken: null, scored: null, total: null, activityScore: 0 });
        setResult([]);
        setSpeechEvaluationScore(0);

        // The mount-only screen effect above doesn't re-run when this Quiz
        // instance is reused for the next chapter's assessment, so it left the
        // student on the PREVIOUS assessment's "Complete" screen showing the
        // previous result. Clear the stale evaluation and reset the screen for
        // the new assessment. The saved-result branch below re-applies them when
        // we intentionally rehydrate an already-completed assessment.
        setEvaluationResult({});
        if (!data.preview) setScreen(ScreenTypes.QuizDetail);

        // If this assessment was already evaluated (result cached locally on the "View
        // Your Score"/result screen), rehydrate and land on the result page instead of
        // restarting the quiz after a reload.
        const savedResult = !data.preview && getAssessmentResult(assessment.id);
        if (savedResult && savedResult.evaluationResult && savedResult.gradingResult) {
            setEvaluationResult(savedResult.evaluationResult);
            setGradingResult(savedResult.gradingResult);
            if (savedResult.result) setResult(savedResult.result);
            setScreen(ScreenTypes.Complete);
            setLoading(false);
            return;
        }

        // The diagnostic test is a one-shot assessment: every attempt must start
        // blank. Restoring previously saved answers was pre-filling old answers
        // when a student retook the diagnostic after logging back in. Clear any
        // cached answers and skip the backend restore for this type.
        if (quizType === 'diagnostic-test-v2') {
            clearAssessmentAnswers(assessment.id);
            setLoading(false);
            return;
        }

        // Load cached answers from backend, but don't auto-fill them on every reload
        // Only save them to localStorage for potential resume functionality
        getAssessmentAnswersActivity(assessment.id).then(resp => {
            let cached = (resp.data || {selectedAnswers: []})["selectedAnswers"];
            saveAssessmentAnswers(assessment.id, cached);
        }).catch(e => {
            console.error(e);
            saveAssessmentAnswers(assessment.id, []);
        }).finally(() => {
            setLoading(false);
        })

    }, [id, data]);

    useEffect(() => {
        const handleEvent = (e) => {
            if (ref.current && ref.current.contains(e.target)) {
                e.preventDefault();
            }
        };

        const beforeUnload = (event) => {
            event.preventDefault()
        };

        const onUnload = () => {
            logoutUser()
        }

        window.addEventListener('unload', onUnload);

        document.addEventListener("contextmenu", handleEvent);
        document.addEventListener("copy", handleEvent);
        document.addEventListener("paste", handleEvent);
        document.addEventListener("selectstart", handleEvent);

        window.addEventListener("beforeunload", beforeUnload);

        return () => {
            document.removeEventListener("contextmenu", handleEvent);
            document.removeEventListener("copy", handleEvent);
            document.removeEventListener("paste", handleEvent);
            document.removeEventListener("selectstart", handleEvent);

            window.removeEventListener("beforeunload", beforeUnload);
            window.removeEventListener('unload', onUnload)
        };
    }, []);

    const screencomponents = {
        [ScreenTypes.QuizDetail]: type === 'diagnostic-test-v2' ? <QuizDiagnosticDetail setAssessmentBegin={setAssessmentBegin} /> : <QuizDetailsScreen style={style} />,
        [ScreenTypes.QuestionScreen]: <QuestionScreen style={style} testType={type} setAssessmentEnded={setAssessmentEnded} />,
        [ScreenTypes.Results]: <Results onComplete={onComplete} onFail={onFail} mandatePassing={mandatePassing} />,
        [ScreenTypes.Complete]: <QuizComplete onComplete={onComplete} mandateSpeechEvaluation={mandateSpeechEvaluation} onFail={onFail} mandatePassing={mandatePassing} onConfettiClose={onConfettiClose} setAssessmentBegin={setAssessmentBegin} type={type} setAssessmentEnded={setAssessmentEnded} setAssessmentScreen={setAssessmentScreen} />,
        [ScreenTypes.Evaluating]: <Evalating />
    }

    if (loading) {
        return <Loader />
    }

    const ComponentToRender = screencomponents[current] || <QuizDetailsScreen style={style} />

    return <div ref={ref}>{ComponentToRender}</div>
}

export default Quiz;
